/**
 * XML Wizard Tab — Merchant Kurulum sayfası (Iter-44 sadeleştirmesi).
 *
 * 4 bölüm (yukarıdan aşağıya):
 *   1) Feed özeti           — Sayz tarafından kurulduğunun rozetli gösterimi
 *   2) Ürün Eşleştirmesi    — Readonly mapping (SKU kilitli badge'i ile)
 *   3) Manuel Eşleştirme    — Eşleştirmeyi Başlat butonu (mevcut mapping ile)
 *   4) Otomatik Eşleştirme  — Aç/Kapa toggle (Sayz preset cron gösterimi)
 *
 * Kaldırılanlar (Iter-44):
 *   • Feed Auth (proje kararı — ihtiyaç olursa geri eklenir)
 *   • Tehlike Bölgesi (Reset destructive op → superadmin'e taşındı)
 *   • Diff Preview, SKU Migration Dialog, URL Swap Dialog (superadmin path)
 */
import { useCallback, useEffect, useState } from "react";
import {
    FileCode, PlayCircle, RefreshCcw, CheckCircle2, AlertTriangle, Loader2,
    X, Package, Tag, ImageIcon, FolderTree, Lock, CalendarClock, Clock,
    LifeBuoy, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useXmlFeed, IN_PROGRESS } from "@/lib/xml-feed";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const SAYZ_FIELDS = [
    { key: "sku",      label: "Ürün Kodu",  locked: true,  icon: Tag,        hint: "Widget bu koda göre eşleşir. Bir kez seçildikten sonra değişmez." },
    { key: "name",     label: "Ürün Adı",   locked: false, icon: Package,    hint: "Panelde ve widget'ta gösterilir." },
    { key: "image",    label: "Fotoğraf",   locked: false, icon: ImageIcon,  hint: "Ürün görselinin XML alanı." },
    { key: "category", label: "Kategori",   locked: false, icon: FolderTree, hint: "Kategori bazlı filtreleme için." },
];

const STATUS_COLORS = {
    idle:        { pill: "bg-muted text-muted-foreground border-border",             label: "Bekleniyor"        },
    queued:      { pill: "bg-info-soft text-info border-info/30",                    label: "Kuyrukta"          },
    downloading: { pill: "bg-info-soft text-info border-info/30",                    label: "İndiriliyor"       },
    parsing:     { pill: "bg-info-soft text-info border-info/30",                    label: "Ayrıştırılıyor"    },
    writing:     { pill: "bg-warning-soft text-warning border-warning/30",           label: "Yazılıyor"         },
    success:     { pill: "bg-success-soft text-success border-success/30",           label: "Başarılı"          },
    failed:      { pill: "bg-destructive/12 text-destructive border-destructive/30", label: "Başarısız"         },
    cancelled:   { pill: "bg-muted text-muted-foreground border-border",             label: "İptal Edildi"      },
};

const CRON_FRIENDLY = {
    "0 3 * * *":    "Her gün 03:00",
    "0 9 * * *":    "Her gün 09:00",
    "0 */6 * * *":  "Her 6 saatte",
    "0 4 * * 1":    "Her Pazartesi 04:00",
    "0 3 1 * *":    "Her ayın 1'i 03:00",
};

const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString("tr-TR") : "—");
const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
};

export default function XmlWizardTab() {
    const { feed, jobStatus, refresh, pollStatus } = useXmlFeed();
    const [step, setStep] = useState("idle");        // idle | syncing
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        if (jobStatus?.status && IN_PROGRESS.has(jobStatus.status)) setStep("syncing");
    }, [jobStatus?.status]);

    const hasFeed = Boolean(feed?.url);
    const hasMapping = Boolean(feed?.mapping?.sku);

    const startSync = useCallback(async () => {
        if (!hasMapping) {
            toast.error("Ürün eşleştirmesi henüz Sayz destek ekibi tarafından kurulmamış.");
            return;
        }
        try {
            setStarting(true);
            await api.post("/xml/feed/sync/start", {
                mapping: feed.mapping,
                category_delimiter: feed.category_delimiter || ">",
                item_path: feed.item_path,
            });
            toast.success("Eşleştirme başlatıldı.");
            setStep("syncing");
            pollStatus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Eşleştirme başlatılamadı.");
        } finally {
            setStarting(false);
        }
    }, [feed, hasMapping, pollStatus]);

    if (feed === null) {
        return (
            <div className="rounded-xl border border-border bg-surface p-8 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Yükleniyor…
            </div>
        );
    }

    if (!hasFeed) {
        return <SetupPendingCard />;
    }

    return (
        <div className="space-y-4">
            <FeedHeader feed={feed} jobStatus={jobStatus} />

            {step === "syncing" ? (
                <SyncingStep
                    job={jobStatus}
                    onCancel={async () => {
                        try {
                            await api.post("/xml/feed/sync/cancel");
                            toast("Eşleştirme iptal edildi.");
                            pollStatus();
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || "İptal edilemedi.");
                        }
                    }}
                    onDone={() => { refresh(); setStep("idle"); }}
                />
            ) : (
                <>
                    <MappingReadonlyCard feed={feed} />
                    <ManualSyncCard canSync={hasMapping} starting={starting} onSync={startSync} feed={feed} />
                    <ScheduleCard feed={feed} refresh={refresh} />
                </>
            )}
        </div>
    );
}

/* ============================================================================
   Kurulum bekleniyor
============================================================================ */
const SetupPendingCard = () => (
    <div className="rounded-xl border border-border bg-surface p-8 text-center" data-testid="xml-setup-pending">
        <div className="h-14 w-14 rounded-xl bg-info-soft text-info grid place-items-center border border-info/25 mx-auto">
            <LifeBuoy className="h-6 w-6" strokeWidth={1.75} />
        </div>
        <h3 className="font-display text-lg font-semibold tracking-tight mt-4">XML kurulumu bekleniyor</h3>
        <p className="text-[12.5px] text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
            Feed URL ve ürün eşleşmesi, kaos ve veri kaybını önlemek için <strong>Sayz destek ekibi</strong> tarafından kurulur.
            Kısa sürede sizinle irtibata geçeceğiz — genelde 10 dakika sürer.
        </p>
    </div>
);

/* ============================================================================
   Feed header
============================================================================ */
const FeedHeader = ({ feed, jobStatus }) => {
    const domain = feed?.url ? (() => { try { return new URL(feed.url).hostname; } catch { return feed.url; } })() : null;
    const status = jobStatus?.status || feed?.last_sync_status || "idle";
    const meta = STATUS_COLORS[status] || STATUS_COLORS.idle;

    return (
        <div className="rounded-xl border border-border bg-surface p-5">
            <div className="flex items-start gap-4 flex-wrap">
                <div className="h-11 w-11 rounded-xl bg-info-soft text-info grid place-items-center shrink-0 border border-info/25">
                    <FileCode className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground">XML Ürün Feed&apos;i</div>
                    <div className="font-display text-lg font-semibold tracking-tight mt-0.5 flex items-center gap-2 flex-wrap">
                        {domain}
                        <Badge variant="outline" className="text-[9.5px] uppercase tracking-wider bg-muted border-border">
                            <Lock className="h-2.5 w-2.5 mr-1" /> Sayz tarafından kuruldu
                        </Badge>
                    </div>
                    <div className="text-[11.5px] text-muted-foreground font-mono truncate mt-0.5 max-w-md" data-testid="xml-feed-url">
                        {feed.url}
                    </div>
                </div>
                <div className="flex items-stretch gap-6 shrink-0">
                    {feed?.total_products > 0 && (
                        <div className="flex flex-col justify-between">
                            <div className="text-[9.5px] font-semibold tracking-wider uppercase text-muted-foreground">Aktif Ürün</div>
                            <div className="text-lg font-bold font-mono tabular-nums leading-tight" data-testid="xml-total-products">{fmtNum(feed.total_products)}</div>
                            <div className="text-[10px] text-muted-foreground opacity-0 select-none">.</div>
                        </div>
                    )}
                    <div className="flex flex-col justify-between">
                        <div className="text-[9.5px] font-semibold tracking-wider uppercase text-muted-foreground">Son Eşleştirme</div>
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider w-fit ${meta.pill}`} data-testid="xml-status-badge">{meta.label}</Badge>
                        <div className="text-[10px] text-muted-foreground font-mono min-h-[14px]">
                            {feed?.last_sync_at ? fmtDate(feed.last_sync_at) : ""}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ============================================================================
   Ürün Eşleştirmesi (readonly)
============================================================================ */
const MappingReadonlyCard = ({ feed }) => {
    if (!feed?.mapping) {
        return (
            <div className="rounded-xl border border-dashed border-border bg-surface p-5 text-center" data-testid="xml-mapping-missing">
                <div className="text-[12.5px] text-muted-foreground">
                    Ürün eşleştirmesi henüz kurulmamış. Sayz destek ekibi kurulumu tamamladığında burada görüntülenecek.
                </div>
            </div>
        );
    }
    const mapping = feed.mapping;
    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="xml-mapping-readonly">
            <div className="px-5 pt-5 pb-3 border-b border-border flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center shrink-0 border border-border">
                    <FolderTree className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                    <h3 className="font-display text-base font-semibold tracking-tight">Ürün Eşleştirmesi</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Sayz destek ekibi tarafından kuruldu. Değişiklik gerekirse destek ekibiyle iletişime geçin.
                    </p>
                </div>
            </div>
            <div className="p-5 space-y-2">
                {SAYZ_FIELDS.map((f) => (
                    <div key={f.key} className="rounded-lg border border-border bg-background px-3 py-2.5 flex items-center gap-3" data-testid={`xml-map-readonly-${f.key}`}>
                        <div className="h-8 w-8 rounded-md bg-muted grid place-items-center shrink-0 border border-border">
                            <f.icon className="h-3.5 w-3.5" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[12.5px] font-semibold">{f.label}</span>
                                {f.locked && (
                                    <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-info-soft text-info border-info/30">
                                        <Lock className="h-2.5 w-2.5 mr-1" /> Kilitli
                                    </Badge>
                                )}
                            </div>
                            <p className="text-[10.5px] text-muted-foreground mt-0.5">{f.hint}</p>
                        </div>
                        <div className="shrink-0">
                            {mapping[f.key] ? (
                                <code className="text-[11.5px] font-mono px-2.5 py-1 rounded bg-muted border border-border">{mapping[f.key]}</code>
                            ) : (
                                <span className="text-[10.5px] text-muted-foreground italic">boş</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ============================================================================
   Manuel Eşleştirme
============================================================================ */
const ManualSyncCard = ({ canSync, starting, onSync, feed }) => (
    <div className="rounded-xl border border-border bg-surface" data-testid="xml-manual-sync-card">
        <div className="px-5 pt-5 pb-3 border-b border-border flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center shrink-0 border border-border">
                <RefreshCcw className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
                <h3 className="font-display text-base font-semibold tracking-tight">Manuel Eşleştirme</h3>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Ürünlerini az önce güncellediysen ve XML kaynağın hazırsa buradan hızlı bir eşleştirme tetikleyebilirsin.
                    Otomatik eşleştirmeler zaten arka planda çalışıyor — bu buton sadece &quot;şimdi de yap&quot; içindir.
                </p>
            </div>
        </div>
        <div className="p-5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11.5px] text-muted-foreground leading-relaxed max-w-lg">
                {canSync ? (
                    <>
                        <ShieldCheck className="h-3.5 w-3.5 text-info inline mr-1" />
                        Ürün alanları (isim / fotoğraf / kategori) güncellenir, atadığın <strong>beden tabloları korunur</strong>. XML&apos;de olmayan ürünler pasife alınır (silinmez, tablo ataması durur).
                    </>
                ) : (
                    <>Ürün eşleştirmesi henüz kurulmamış — Sayz destek ekibi feed hazırlığını tamamlıyor.</>
                )}
                {feed?.last_sync_at && (
                    <div className="text-[10.5px] text-muted-foreground mt-1.5">
                        Son eşleştirme: <span className="font-mono">{fmtDate(feed.last_sync_at)}</span>
                    </div>
                )}
            </div>
            <Button onClick={onSync} disabled={starting || !canSync} data-testid="xml-manual-sync-btn">
                {starting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
                Eşleştirmeyi Başlat
            </Button>
        </div>
    </div>
);

/* ============================================================================
   Otomatik Eşleştirme (schedule aç/kapa)
============================================================================ */
const ScheduleCard = ({ feed, refresh }) => {
    const saved = feed?.schedule || { enabled: false, cron: null, timezone: "Europe/Istanbul" };
    const [enabled, setEnabled] = useState(saved.enabled);
    const [busy, setBusy] = useState(false);

    useEffect(() => { setEnabled(!!saved.enabled); }, [saved.enabled]);

    const toggle = async (next) => {
        try {
            setBusy(true);
            setEnabled(next);   // optimistic
            const { data } = await api.put("/xml/feed/schedule-v2", {
                enabled: next,
                cron: saved.cron,
                timezone: saved.timezone,
            });
            toast.success(next
                ? (data?.next_run_at
                    ? `Otomatik eşleştirme açıldı. İlk çalışma: ${fmtDate(data.next_run_at)}`
                    : "Otomatik eşleştirme açıldı.")
                : "Otomatik eşleştirme kapatıldı."
            );
            refresh();
        } catch (e) {
            setEnabled(!next);
            toast.error(e?.response?.data?.detail || "Kaydedilemedi.");
        } finally {
            setBusy(false);
        }
    };

    const scheduleReady = Boolean(saved.cron);
    const label = scheduleReady ? (CRON_FRIENDLY[saved.cron] || saved.cron) : "—";

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="xml-schedule-card">
            <div className="px-5 pt-5 pb-3 border-b border-border flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center shrink-0 border border-border">
                    <CalendarClock className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div>
                    <h3 className="font-display text-base font-semibold tracking-tight">Otomatik Eşleştirme</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Sayz destek ekibi tarafından ayarlanan zamanlarda XML&apos;in otomatik olarak eşleştirilir.
                        Sen sadece aç/kapa yapabilirsin.
                    </p>
                </div>
            </div>
            <div className="p-5">
                {scheduleReady ? (
                    <div className="rounded-lg border border-border bg-background p-4 flex items-center gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-[10.5px]">{label}</Badge>
                                <span className="text-[11px] text-muted-foreground font-mono">{saved.timezone || "Europe/Istanbul"}</span>
                            </div>
                            {enabled && saved.next_run_at && (
                                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5 mt-1.5" data-testid="xml-schedule-next-run">
                                    <Clock className="h-3 w-3" /> Sıradaki çalışma: <span className="font-mono text-foreground/85">{fmtDate(saved.next_run_at)}</span>
                                </div>
                            )}
                        </div>
                        <label className="inline-flex items-center gap-2.5 cursor-pointer" data-testid="xml-schedule-enable-label">
                            <input
                                type="checkbox"
                                checked={enabled}
                                disabled={busy}
                                onChange={(e) => toggle(e.target.checked)}
                                className="h-4 w-4 rounded border-border"
                                data-testid="xml-schedule-enabled"
                            />
                            <span className="text-[12.5px] font-medium">
                                {busy ? (
                                    <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor…</span>
                                ) : enabled ? "Otomatik eşleştirme açık" : "Otomatik eşleştirme kapalı"}
                            </span>
                        </label>
                    </div>
                ) : (
                    <div className="rounded-lg border border-dashed border-border p-4 text-center text-[12px] text-muted-foreground" data-testid="xml-schedule-not-configured">
                        Otomatik eşleştirme zamanlaması henüz Sayz destek ekibi tarafından ayarlanmadı.
                    </div>
                )}
            </div>
        </div>
    );
};

/* ============================================================================
   Eşleştirme devam ediyor / bitti
============================================================================ */
const SyncingStep = ({ job, onCancel, onDone }) => {
    const status = job?.status || "queued";
    const inProgress = IN_PROGRESS.has(status);
    const isDone = ["success", "failed", "cancelled"].includes(status);
    const meta = STATUS_COLORS[status] || STATUS_COLORS.idle;
    const pct = Math.min(100, Math.max(0, job?.progress_pct || 0));

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="xml-syncing-step">
            <div className="px-5 pt-5 pb-3 border-b border-border flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center shrink-0 border border-border">
                    {inProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : status === "success" ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4 text-destructive" />}
                </div>
                <div className="min-w-0">
                    <h3 className="font-display text-base font-semibold tracking-tight">
                        {inProgress ? "Eşleştirme Devam Ediyor…" : status === "success" ? "Eşleştirme Tamamlandı" : status === "cancelled" ? "İptal Edildi" : "Eşleştirme Başarısız"}
                    </h3>
                    {inProgress && <p className="text-xs text-muted-foreground mt-0.5">Bu sayfayı kapatabilirsin — arka planda devam eder.</p>}
                </div>
            </div>
            <div className="p-5 space-y-4">
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${meta.pill}`} data-testid="xml-sync-status-pill">{meta.label}</Badge>
                        <span className="text-[13px] font-bold font-mono tabular-nums" data-testid="xml-sync-pct">%{Math.round(pct)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full transition-all ${status === "success" ? "bg-success" : status === "failed" || status === "cancelled" ? "bg-destructive" : "bg-info"}`} style={{ width: `${pct}%` }} data-testid="xml-sync-progress-bar" />
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatBox label="Görüldü"        value={job?.total_seen} />
                    <StatBox label="Yeni Eklenen"   value={job?.total_new}      tone="success" />
                    <StatBox label="Güncellenen"    value={job?.total_updated}  tone="info" />
                    <StatBox label="Pasife Alınan"  value={job?.total_archived} tone="warning" />
                </div>
                {job?.error && (
                    <div className="rounded-lg border border-destructive/40 bg-destructive/8 p-3 flex items-start gap-2.5">
                        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <div className="text-[12.5px] font-semibold text-destructive">Hata Detayı</div>
                            <div className="text-[11.5px] text-foreground/85 mt-1 font-mono break-all" data-testid="xml-sync-error">{job.error}</div>
                        </div>
                    </div>
                )}
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
                    {inProgress ? (
                        <Button variant="outline" onClick={onCancel} data-testid="xml-sync-cancel"><X className="h-3.5 w-3.5 mr-1.5" /> İptal Et</Button>
                    ) : (<div />)}
                    {isDone && (<Button onClick={onDone} data-testid="xml-sync-done"><RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Kapat</Button>)}
                </div>
            </div>
        </div>
    );
};

const TONE_CLASSES = {
    default: "text-foreground",
    success: "text-success",
    info:    "text-info",
    warning: "text-warning",
    muted:   "text-muted-foreground",
};
const StatBox = ({ label, value, tone = "default" }) => (
    <div className="rounded-lg border border-border bg-background p-3">
        <div className="text-[9.5px] font-semibold tracking-[0.12em] uppercase text-muted-foreground">{label}</div>
        <div className={`text-lg font-bold font-mono tabular-nums leading-tight mt-1 ${TONE_CLASSES[tone]}`}>{fmtNum(value || 0)}</div>
    </div>
);
